import React, { useMemo } from 'react'
import { Layout, Typography, Tooltip } from 'antd'
import styledComponents from 'styled-components'
import { TopMenu } from './top-menu'
import { UserProfile } from './user-profile'
import UpdatesChecker from './updatesChecker'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { getDataOfType } from 'src/lib/utils/get-data-of-type'
import { useHeaderActions } from '../../contexts/header-actions-context'
import { useMediaQuery } from 'react-responsive'
import { LeftOutlined } from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { HOME_URL, selectIsTenantOwner, selectStartPage } from '../../pages_v2/home/owner-access'

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

interface IBreadcrumb {
  href?: string
  title: string
}

interface HeaderProps {
  pageTitle?: string
  breadcrumbs?: IBreadcrumb[]
}

const StyledHeader = styledComponents(Layout.Header)`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 901;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  border-bottom: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    padding: 12px 16px;
    height: auto;
    min-height: 64px;
  }
`

const LeftSection = styledComponents.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const BackRow = styledComponents.div``

const HeaderInfo = styledComponents.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    flex: 1;
  }
`

const RightSection = styledComponents.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`

const ActionButton = styledComponents.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  transition: color 0.3s;

  &:hover {
    color: #8b9ca7;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const PageTitle = styledComponents(Typography.Title)`
  margin: 0 !important;
  font-size: 20px !important;

  @media (max-width: 768px) {
    font-size: 16px !important;
  }
`

const OfficeName = styledComponents(Typography.Text)`
  font-size: 12px;
  color: #00000072;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

export const Header: React.FC<HeaderProps> = ({ pageTitle, breadcrumbs = [] }) => {
  const { sessionData } = useSelector(createSession)
  const { headerActions } = useHeaderActions()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const navigate = useNavigate()
  const location = useLocation()
  const isTenantOwner = useSelector(selectIsTenantOwner)
  const startPage = useSelector(selectStartPage)

  const officeName = useMemo(() => {
    return getDataOfType(sessionData, 'user.office.name', String, '')
  }, [sessionData])

  const validBreadcrumbs = useMemo(() => breadcrumbs.filter(Boolean), [breadcrumbs])
  const showBackButton = validBreadcrumbs.length >= 2

  /**
   * Назад — по истории браузера. Если внутри приложения истории нет
   * (страницу открыли напрямую / в новой вкладке) — на главную /home (владельцу)
   * или на стартовую страницу (остальным).
   */
  const handleBackClick = () => {
    const historyIdx = (window.history.state as { idx?: number } | null)?.idx
    const hasInAppHistory =
      typeof historyIdx === 'number' ? historyIdx > 0 : location.key !== 'default'
    if (hasInAppHistory) {
      navigate(-1)
      return
    }
    navigate(isTenantOwner ? HOME_URL : startPage, { replace: true })
  }

  return (
    <StyledHeader>
      <LeftSection>
        {showBackButton && (
          <BackRow>
            <ActionButton onClick={handleBackClick}>
              <LeftOutlined />
            </ActionButton>
          </BackRow>
        )}
        <HeaderInfo>
          {officeName && <OfficeName>{officeName}</OfficeName>}
          {pageTitle && <PageTitle level={3}>{pageTitle}</PageTitle>}
        </HeaderInfo>
      </LeftSection>
      <RightSection>
        {isMobile ? (
          headerActions.map(action => (
            <Tooltip key={action.key} title={action.tooltip}>
              <ActionButton
                onClick={action.onClick}
                data-tour-id={action.key === 'filters' ? 'appeals-filters-button' : undefined}
              >
                {action.icon}
              </ActionButton>
            </Tooltip>
          ))
        ) : (
          <>
            <TopMenu />
            <UserProfile />
            <UpdatesChecker />
          </>
        )}
      </RightSection>
    </StyledHeader>
  )
}
